import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { translateRoute } from '../utils/routeTranslator';

/**
 * Professional Language Management Hook using i18next
 * Handles language switching while preserving current route
 */
export const useLanguage = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Sync i18n language with URL
  useEffect(() => {
    const pathLang = location.pathname.split('/')[1];
    
    if ((pathLang === 'en' || pathLang === 'ar') && i18n.language !== pathLang) {
      i18n.changeLanguage(pathLang);
    }
  }, [location.pathname, i18n]);

  /**
   * Changes language while preserving current route
   * @param {string} targetLang - Target language ('en' or 'ar')
   */
  const changeLanguage = (targetLang) => {
    if (targetLang !== 'en' && targetLang !== 'ar') {
      console.warn(`Invalid language: ${targetLang}`);
      return;
    }

    // Don't change if already in target language
    if (i18n.language === targetLang) {
      return;
    }

    // Translate current route to target language
    // React Router's location.pathname is already decoded
    const currentPath = location.pathname;
    const translatedPath = translateRoute(currentPath, targetLang);
    
    // Debug log (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('Language change:', {
        from: i18n.language,
        to: targetLang,
        currentPath,
        translatedPath
      });
    }

    // Change language in i18n
    i18n.changeLanguage(targetLang);

    // Navigate to translated route
    navigate(translatedPath, { replace: true });
  };

  return {
    currentLanguage: i18n.language,
    changeLanguage,
    isEnglish: i18n.language === 'en',
    isArabic: i18n.language === 'ar',
    t: i18n.t.bind(i18n)
  };
};
